import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * Routes whose content never reaches Mixpanel replay, heatmaps or autocapture
 * (D-4, design 2026-09-22 §5.2, M12), plus any path with a `documents` segment.
 * `/company` joined on the owner's F5 answer (2026-09-23): it shows the
 * company's Documents tab and shareholder details.
 *
 * Each prefix is matched as whole path segments (`/company`, `/company/…`),
 * never as a bare string prefix, so `/companies` or `/companyX` stay tracked.
 *
 * This module has no Mixpanel import, so CustomerLayout can use it statically
 * while MixpanelInit stays in its own dynamically imported chunk.
 */
export const SENSITIVE_ANALYTICS_PREFIXES = [
  '/onboarding',
  '/federal-tax',
  '/state-tax',
  '/state-filings',
  '/company',
] as const

/**
 * The routes isSensitiveAnalyticsPath covers, as a URL pattern for Mixpanel's
 * `autocapture.block_url_regexes`, which mixpanel-browser checks against the
 * page URL on every autocaptured event. It matches a sensitive segment anywhere
 * in the URL, so it can only over-block, never under-block.
 */
export const SENSITIVE_URL_PATTERN = /\/(?:onboarding|federal-tax|state-tax|state-filings|company|documents)(?:[/?#]|$)/

/**
 * rrweb's block class, passed to Mixpanel as `record_block_class`. An element
 * carrying it is recorded as an empty placeholder and nothing inside it is ever
 * serialized, including nodes added later. It is the one control that holds
 * however recording timing falls: a recording that resumes asynchronously on a
 * full load of a sensitive route (mixpanel-browser resumes an active tab's
 * recording from IndexedDB regardless of the sample rate) still sees the class.
 * `mp-block` is also in mixpanel-browser 2.83.0's default block regex.
 */
export const SESSION_RECORDING_BLOCK_CLASS = 'mp-block'

/**
 * Marks an element whose url is itself a credential: a signed document link
 * (`/api/objects/signed?key&exp&sig` opens the file with no session) or a
 * hosted invoice page. Spread `privateAnalyticsProps` on it. Autocapture then
 * drops any click on or inside it and session replay records it as an empty
 * box, on every route; the route rules above only cover sensitive pages, and
 * the dashboard and mailroom are not among them.
 */
export const PRIVATE_ANALYTICS_ATTRIBUTE = 'data-analytics-private'
export const privateAnalyticsProps = { [PRIVATE_ANALYTICS_ATTRIBUTE]: '' } as const

const SIGNED_OBJECT_PATH = '/api/objects/signed'

/**
 * Mixpanel `autocapture.block_selectors`: the marker, plus any signed object
 * link or embed that was rendered without it.
 */
export const PRIVATE_ANALYTICS_SELECTORS = [
  `[${PRIVATE_ANALYTICS_ATTRIBUTE}]`,
  `[href*="${SIGNED_OBJECT_PATH}"]`,
  `[src*="${SIGNED_OBJECT_PATH}"]`,
]

/**
 * Mixpanel `record_block_selector`. Setting it replaces the SDK default
 * (`img, video, audio`), so those stay. rrweb records a matching element as an
 * empty box without its attributes; without this, an anchor's `href` keeps its
 * query and a cross-origin iframe's `src` is kept as `rr_src`. Every iframe is
 * blocked: the only ones the app renders are document previews.
 */
export const SESSION_RECORDING_BLOCK_SELECTOR = [
  'img',
  'video',
  'audio',
  'iframe',
  ...PRIVATE_ANALYTICS_SELECTORS,
].join(', ')

export function isSensitiveAnalyticsPath(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return false
  }

  if (pathname.split('/').includes('documents')) {
    return true
  }

  return SENSITIVE_ANALYTICS_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

/**
 * The root of every customer page. On a sensitive route it carries the session
 * recording block class; `contents` keeps it out of layout either way, and it
 * renders on every route so crossing a sensitive boundary never remounts the
 * page below it.
 */
export function SessionRecordingBoundary({ pathname, children }: { pathname: string; children: ReactNode }) {
  return (
    <div className={cn('contents', isSensitiveAnalyticsPath(pathname) && SESSION_RECORDING_BLOCK_CLASS)}>
      {children}
    </div>
  )
}
